import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Sheet docking, expressed LOGICALLY.
 *
 * `inline-start` / `inline-end` rather than `left` / `right`: a filter drawer
 * must open from the edge the reader's eye leaves from, which is the right edge
 * in Arabic and the left in English. The physical form would also be a lint
 * error (U7) — the point of naming the props this way is that a caller cannot
 * even ask for the wrong thing.
 *
 * The entry animation uses the preset's logical keyframes, driven by the
 * `--inline-start-sign` custom property that `globals.css` flips under
 * `[dir='rtl']`. `slide-in-from-left-2` would slip past the class-law regex and
 * be wrong in Arabic — see the preset's amendment note.
 *
 * ---------------------------------------------------------------------------
 * AMENDED 2026-07-30 — the sheet had NO scroll story. `Dialog` clamps itself
 * (`max-h-dialog overflow-hidden`) and scrolls inside `DialogBody`; the sheet
 * shipped with no `overflow`, no `min-h-0`, no body region, and no height cap on
 * the block-axis sides at all — so tall content grew straight out of the panel
 * and `SheetFooter`'s `mt-auto` meant nothing. Latent only because `MobileNav`,
 * its single consumer, has a short nav list. `overflow-hidden` on the base plus
 * `max-h-dialog` on top/bottom makes the panel a real box; `SheetBody` is the
 * one thing inside it that scrolls (U0: a pane contains its own content).
 *
 * AMENDED 2026-08-02 (P10.1) — the panel's `overflow-hidden` became conditional
 * on a `SheetBody` being present, for the reason spelled out in
 * `DIALOG_CONTAINMENT_CLASSES`: hiding the overflow of a box with nothing
 * scrollable inside it does not contain the content, it CUTS IT OFF. `MobileNav`
 * happened to be safe (its nav list scrolls), which is precisely the kind of
 * accident that stops being true the first time someone composes a sheet
 * without one.
 * ---------------------------------------------------------------------------
 */
export const sheetVariants = cva(
  [
    'fixed z-modal flex flex-col gap-lg bg-card p-xl shadow-xl transition ease-standard',
    // Clip when something inside scrolls; scroll as one piece when nothing does.
    'has-[[data-sheet-body]]:overflow-hidden',
    '[&:not(:has([data-sheet-body]))]:overflow-y-auto',
    '[&:not(:has([data-sheet-body]))]:overscroll-contain',
  ].join(' '),
  {
    variants: {
      side: {
        // `w-full` below `sm`: the panel is 420px wide, which is a hundred
        // pixels wider than a 320px phone — it hung off the inline edge in both
        // directions until the P10.1 walk measured it. A side sheet on a phone
        // is the whole screen; above `sm` it is a column beside the page.
        'inline-start':
          'inset-y-0 start-0 h-full w-full sm:w-dialog-sm border-e border-border data-[state=open]:animate-slide-in-from-inline-start data-[state=closed]:animate-slide-out-to-inline-start',
        'inline-end':
          'inset-y-0 end-0 h-full w-full sm:w-dialog-sm border-s border-border data-[state=open]:animate-slide-in-from-inline-end data-[state=closed]:animate-slide-out-to-inline-end',
        // The inline sides are already bounded by `inset-y-0 h-full`; the block
        // sides are bounded by nothing, so they take the dialog clamp.
        top: 'inset-x-0 top-0 max-h-dialog border-b border-border data-[state=open]:animate-in data-[state=open]:slide-in-from-top data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top',
        bottom:
          'inset-x-0 bottom-0 max-h-dialog border-t border-border data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
      },
    },
    defaultVariants: { side: 'inline-end' },
  },
);

export type SheetVariants = VariantProps<typeof sheetVariants>;
